import { jsx as _jsx } from "react/jsx-runtime";
import { AsyncSelect as RlAsyncSelect, selectComponents } from '@rocketlane/ui';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { components as RsComponents } from 'react-select';
import { GroupComponentForSelect } from 'components/molecules/group-select/group-select';
import { useAccountFeature, AccountFeature } from 'hooks/use-account-feature';
import { useTranslate } from 'hooks/use-translation';
import { useGroupsQuery } from 'queries/groups';
import { useAsyncNativeUsers } from 'queries/members';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { FlexUserComponents, TagComponents } from './select';
const AsyncSelect = RlAsyncSelect;
// Custom option component that renders differently for users vs groups.
// User rows use the flex-safe option (FlexUserComponents.Option) so they don't
// collapse inside the package's flex `.ozone-select__option`; group rows are
// plain package rows (menu tag casing is deprecated — chips stay on the
// selected values only).
const UserGroupOption = (props)=>{
    const { data } = props;
    if (data.type === 'user') {
        return /*#__PURE__*/ _jsx(FlexUserComponents.Option, {
            ...props
        });
    }
    if (data.type === 'group') {
        return /*#__PURE__*/ _jsx(selectComponents.Option, {
            ...props
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        children: data.label
    });
};
// Custom single value component that renders differently for users vs groups
const UserGroupSingleValue = (props)=>{
    const { data } = props;
    if (data.type === 'user') {
        return /*#__PURE__*/ _jsx(FlexUserComponents.SingleValue, {
            ...props
        });
    }
    if (data.type === 'group') {
        return /*#__PURE__*/ _jsx(TagComponents.SingleValue, {
            ...props
        });
    }
    return /*#__PURE__*/ _jsx(RsComponents.SingleValue, {
        ...props,
        children: data.label
    });
};
// Custom multi value component that renders differently for users vs groups
const UserGroupMultiValue = (props)=>{
    const { data } = props;
    if (data.type === 'user') {
        return /*#__PURE__*/ _jsx(FlexUserComponents.MultiValue, {
            ...props
        });
    }
    if (data.type === 'group') {
        return /*#__PURE__*/ _jsx(TagComponents.MultiValue, {
            ...props
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        children: data.label
    });
};
const UserGroupMultiValueLabel = (props)=>{
    const { data } = props;
    if ([
        'user',
        'group'
    ].includes(data.type)) {
        var _ref, _ref1;
        return /*#__PURE__*/ _jsx(FlexUserComponents.MultiValueLabel, {
            ...props,
            data: {
                ...data,
                hideAvatar: (_ref = (_ref1 = data.type && data.type === 'group') !== null && _ref1 !== void 0 ? _ref1 : data.hideAvatar) !== null && _ref !== void 0 ? _ref : false
            }
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        children: data.label
    });
};
export const UserGroupSelect = ({ showUsers = true, showGroups = true, filterFnUsers, ...selectProps })=>{
    // Fetch users using paginated API with async search
    const { defaultOptions: users, loadOptions: loadUserOptions } = useAsyncNativeUsers(filterFnUsers, undefined, true);
    const { data: groups = [] } = useGroupsQuery();
    const [restrictUserGroupsFeature] = useAccountFeature(AccountFeature.RestrictUserGroups);
    const translate = useTranslate();
    // Transform users for the UserGroupSelect
    const userOptions = useMemo(()=>users.map((user)=>{
            var _user_role;
            return {
                type: 'user',
                label: fullName(user),
                value: `user_${user.userId}`,
                imageUrl: user.photoUrl,
                userStatus: user.userStatus,
                description: (_user_role = user.role) === null || _user_role === void 0 ? void 0 : _user_role.roleName,
                userId: user.userId
            };
        }), [
        users
    ]);
    const groupOptions = useMemo(()=>groups.map((group)=>({
                type: 'group',
                label: group.groupName,
                value: `group_${group.groupId}`,
                color: getRandomColor(group.groupId),
                groupId: group.groupId
            })), [
        groups
    ]);
    // Default options (initial load - users and groups)
    const defaultOptions = useMemo(()=>{
        const groupedOptions = [];
        if (showUsers && users.length > 0) {
            groupedOptions.push({
                label: translate('team_members'),
                options: userOptions.map((uo)=>({
                        ...uo,
                        type: 'user'
                    }))
            });
        }
        if (showGroups && groups.length > 0 && !restrictUserGroupsFeature) {
            groupedOptions.push({
                label: translate('groups'),
                options: groupOptions.map((go)=>({
                        ...go,
                        type: 'group'
                    }))
            });
        }
        return groupedOptions;
    }, [
        showUsers,
        users.length,
        showGroups,
        groups.length,
        restrictUserGroupsFeature,
        translate,
        userOptions,
        groupOptions
    ]);
    // Load options wrapper for async search (handles both users and groups)
    const loadOptionsWithGroups = useCallback(async (input)=>{
        const userResults = await loadUserOptions(input);
        const groupedResults = [];
        // Transform User[] to UserOption[]
        const transformedUsers = userResults.map((user)=>{
            var _user_role;
            return {
                type: 'user',
                label: fullName(user),
                value: `user_${user.userId}`,
                imageUrl: user.photoUrl,
                userStatus: user.userStatus,
                description: (_user_role = user.role) === null || _user_role === void 0 ? void 0 : _user_role.roleName,
                userId: user.userId
            };
        });
        if (showUsers && transformedUsers.length > 0) {
            groupedResults.push({
                label: translate('team_members'),
                options: transformedUsers
            });
        }
        if (showGroups && groups.length > 0 && !restrictUserGroupsFeature) {
            const filteredGroups = groups.filter((g)=>g.groupName.toLowerCase().includes(input.toLowerCase()));
            if (filteredGroups.length > 0) {
                groupedResults.push({
                    label: translate('groups'),
                    options: filteredGroups.map((group)=>({
                            type: 'group',
                            label: group.groupName,
                            value: `group_${group.groupId}`,
                            color: getRandomColor(group.groupId),
                            groupId: group.groupId
                        }))
                });
            }
        }
        return groupedResults;
    }, [
        loadUserOptions,
        showUsers,
        showGroups,
        groups,
        restrictUserGroupsFeature,
        translate
    ]);
    const customComponents = {
        Option: UserGroupOption,
        SingleValue: UserGroupSingleValue,
        MultiValue: UserGroupMultiValue,
        MultiValueLabel: UserGroupMultiValueLabel,
        Group: GroupComponentForSelect
    };
    const selectComponent = /*#__PURE__*/ _jsx(AsyncSelect, {
        classNamePrefix: "ozone-select",
        noOptionsMessage: ()=>/*#__PURE__*/ _jsx(FormattedMessage, {
                id: "no.options"
            }),
        ...selectProps,
        isMulti: true,
        defaultOptions: defaultOptions,
        loadOptions: loadOptionsWithGroups,
        cacheOptions: true,
        components: customComponents,
        isSearchable: true,
        placeholder: translate('placeholder.search'),
        closeMenuOnSelect: false
    });
    return selectComponent;
};
UserGroupSelect.displayName = 'UserGroupSelect';
